import {DestroyRef, inject, Injectable} from '@angular/core';
import {BehaviorSubject, map, merge, Observable, of, switchMap, tap,} from 'rxjs';
import {Booking} from '../../domain/models/booking.model';
import {BOOKING_REPOSITORY} from '../../domain/tokens/booking.tokens';
import {BOOKING_SESSION} from '../../domain/tokens/booking-session.token';
import {EVENT_BUS} from '../../../../../shared/domain/tokens/event-bus.token';
import {BookingLockCheckedEvent} from '../../../../../shared/application/events/booking/booking-lock-checked.event';
import {BookingUpdatedEvent} from '../../../../../shared/application/events/booking/booking-updated.event';
import {BookingDeactivatedEvent} from '../../../../../shared/application/events/booking/booking-deactivated.event';
import {BookingFetchedEvent} from 'src/app/shared/application/events/booking/booking-fetched.event';
import {PaymentBookingCompletedEvent} from "../../../payment/application/events/payment-booking-completed.event";
import {BookingClosedEvent} from "../../../../../shared/application/events/booking/booking-closed.event";
import {BookingDisabledEvent} from "../../../../../shared/application/events/booking/booking-disabled.event";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  BookingActivationFinishedEvent
} from "../../../../../shared/application/events/booking/booking-activation-finished.event";

type DomainEventType =
  BookingUpdatedEvent
  | BookingActivationFinishedEvent
  | BookingDeactivatedEvent
  | PaymentBookingCompletedEvent
  | BookingClosedEvent;

@Injectable({providedIn: 'root'})
export class GetActiveBookingUseCase {
  private readonly eventBus = inject(EVENT_BUS);
  private readonly bookingRepository = inject(BOOKING_REPOSITORY);
  private readonly bookingSessionService = inject(BOOKING_SESSION);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _activeBooking$ = new BehaviorSubject<Booking | null>(null);

  constructor() {
    this.loadInitialBooking();
    this.listenToBookingEvents();
  }

  execute(): Observable<Booking | null> {
    return this._activeBooking$.asObservable();
  }

  private loadInitialBooking(): void {
    this.bookingRepository.getActive()
      .pipe(
        tap((booking) => this.updateActiveBooking(booking)),
        tap((booking) => {
          if (booking?.isActive) {
            this.eventBus.publish(new BookingFetchedEvent(booking));
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenToBookingEvents(): void {
    merge(
      this.eventBus.ofType(BookingActivationFinishedEvent),
      this.eventBus.ofType(BookingUpdatedEvent),
      this.eventBus.ofType(BookingDeactivatedEvent),
      this.eventBus.ofType(PaymentBookingCompletedEvent),
    )
      .pipe(
        switchMap((event) => this.handleBookingEvent(event)),
        tap((booking) => this.updateActiveBooking(booking)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private handleBookingEvent(event: DomainEventType): Observable<Booking | null> {
    const api$ = this.bookingSessionService.get().pipe(
      switchMap((activeBookingId) =>
        this.bookingRepository.getActive().pipe(
          map(booking => (booking && booking.id === activeBookingId ? booking : null))
        )
      )
    );

    if (event instanceof BookingUpdatedEvent && event.optimistic) {
      return of(event.booking);
    }

    if (event instanceof BookingDeactivatedEvent) {
      return of(null);
    }

    return api$;
  }

  private updateActiveBooking(booking: Booking | null): void {
    this._activeBooking$.next(booking);

    if (!booking) {
      return;
    }

    if (!booking.isActive) {
      this.eventBus.publish(
        new BookingLockCheckedEvent({isLocked: true, correlationId: ''})
      );
    }

    if (booking.isClosed) {
      this.eventBus.publish(new BookingDisabledEvent());
    }

  }
}
